/**
 * Hook for upload submission logic
 * Centralises file/text API calls, analytics side-effects, and error mapping
 */

import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import * as apiClient from "@/services/api-client";
import {
  trackExtractionFailed,
  trackExtractionStarted,
  trackExtractionSuccess,
  trackUploadFailed,
  trackUploadStarted,
  trackUploadSuccess,
} from "@/services/analytics";
import {
  toContextRequest,
  type AuditContextFormValues,
} from "@/features/upload/audit-context-form";
import type { TextInputData } from "@/features/upload/components/TextInput";
import type { UploadInputMode as InputMode } from "@/features/upload/components/UploadInputModeTabs";
import type { UrlScanFailureCode } from "@/types";
import { getStoredReportLanguage } from "@/utils/language-preference";

/** URL-scan input: the target page + the Tier-A attestation (pirx-27tn.7). */
export type UrlInputData = { url: string; attestation: boolean };

const URL_SCAN_FAILURE_CODES: readonly UrlScanFailureCode[] = [
  "LOGIN_WALL",
  "BLOCKED",
  "ROBOTS_DISALLOWED",
  "EMPTY_AFTER_EXTRACTION",
  "FETCH_ERROR",
];

function toUrlScanFailureCode(value: unknown): UrlScanFailureCode | undefined {
  return typeof value === "string" &&
      (URL_SCAN_FAILURE_CODES as readonly string[]).includes(value)
    ? value as UrlScanFailureCode
    : undefined;
}

type UploadConflictItem = { fileName: string; existingStatus: string };

function isUploadConflictItem(value: unknown): value is UploadConflictItem {
  return typeof value === "object" &&
    value !== null &&
    "fileName" in value &&
    typeof value.fileName === "string" &&
    "existingStatus" in value &&
    typeof value.existingStatus === "string";
}

/** Shared by the mixed-batch success path and the all-duplicates 409 path. */
function formatConflictMessages(
  conflicts: readonly UploadConflictItem[],
  t: (key: string) => string,
): string {
  return conflicts
    .map((conflict) =>
      `${conflict.fileName} (${t("history:table.status")}: ${
        t(`history:status.${conflict.existingStatus}`)
      })`
    )
    .join("\n");
}

type UseUploadSubmitOptions = {
  inputMode: InputMode;
  selectedFiles: File[];
  textData: TextInputData | null;
  urlData?: UrlInputData | null;
  contextValues: AuditContextFormValues;
  /** When true and the batch is multiple images, the backend will create a
   * single audit covering all selected images instead of one per file. */
  groupImages?: boolean;
  onError: (message: string) => void;
  onNavigate: (path: string, state: { successMessage: string }) => void;
};

// --- analytics helpers ---

function fireStarted(): void {
  trackUploadStarted().catch((err) =>
    console.error("Failed to track upload started:", err)
  );
  trackExtractionStarted().catch((err) =>
    console.error("Failed to track extraction started:", err)
  );
}

function fireSuccess(size: number, ext: string): void {
  trackUploadSuccess(size, ext).catch((err) =>
    console.error("Failed to track upload success:", err)
  );
  trackExtractionSuccess(0).catch((err) =>
    console.error("Failed to track extraction success:", err)
  );
}

function fireUploadFailed(reason: string): void {
  trackUploadFailed(reason).catch((err) =>
    console.error("Failed to track upload failure:", err)
  );
}

function fireBothFailed(reason: string): void {
  trackUploadFailed(reason).catch((err) =>
    console.error("Failed to track upload failure:", err)
  );
  trackExtractionFailed(reason).catch((err) =>
    console.error("Failed to track extraction failure:", err)
  );
}

export function useUploadSubmit({
  inputMode,
  selectedFiles,
  textData,
  urlData = null,
  contextValues,
  groupImages,
  onError,
  onNavigate,
}: UseUploadSubmitOptions) {
  const { t } = useTranslation(["upload", "errors", "history"]);
  const [isUploading, setIsUploading] = useState(false);

  const handleSubmit = useCallback(async () => {
    if (inputMode === "file" && selectedFiles.length === 0) return;
    if (inputMode === "text" && !textData) return;
    if (
      inputMode === "url" &&
      (!urlData || !urlData.url.trim() || !urlData.attestation)
    ) {
      return;
    }

    setIsUploading(true);
    fireStarted();

    const reportLanguage = getStoredReportLanguage();
    const context = toContextRequest(contextValues);

    try {
      if (inputMode === "file") {
        const response = await apiClient.uploadAndAnalyze(
          selectedFiles,
          context,
          reportLanguage,
          { groupImages: Boolean(groupImages) },
        );

        const firstFile = selectedFiles[0];
        fireSuccess(firstFile.size, firstFile.name.split(".").pop() || "unknown");

        const queuedCount = response.audits.filter((a) =>
          a.status === "QUEUED"
        ).length;
        const skippedCount = response.audits.filter((a) =>
          a.status === "SKIPPED"
        ).length;

        let message = t("upload:success.filesQueued", { count: queuedCount });
        if (skippedCount > 0) {
          message += " " +
            t("upload:success.filesSkipped", { count: skippedCount });
        }
        if (response.conflicts && response.conflicts.length > 0) {
          // Mixed-batch 201 (pirx-review-2026-08-02 #2): some files queued,
          // some dropped as duplicates. The duplicates never became audits
          // at all, so unlike the QUEUED/SKIPPED counts above, they need
          // their own mention or the user has no signal they were dropped.
          message += "\n\n" +
            t("upload:errors.duplicateMessage", {
              conflicts: formatConflictMessages(response.conflicts, t),
            });
        }
        onNavigate("/history", { successMessage: message });
      } else if (inputMode === "text") {
        await apiClient.submitTextForAnalysis({
          text: textData!.text,
          title: textData!.title,
          context,
          language: reportLanguage,
        });

        fireSuccess(new TextEncoder().encode(textData!.text).length, "txt");
        onNavigate("/history", {
          successMessage: t("upload:textInput.success.message"),
        });
      } else {
        await apiClient.submitUrlForAnalysis({
          url: urlData!.url.trim(),
          attestation: urlData!.attestation,
          context,
          language: reportLanguage,
        });

        fireSuccess(
          new TextEncoder().encode(urlData!.url).length,
          "url",
        );
        onNavigate("/history", {
          successMessage: t("upload:urlInput.success.message"),
        });
      }
    } catch (err) {
      const failureCode = err instanceof apiClient.ApiError
        ? toUrlScanFailureCode(err.response?.failureCode)
        : undefined;
      if (failureCode) {
        // Fail-loud URL-scan taxonomy → the specific inline message.
        onError(t(`upload:urlInput.errors.${failureCode}`));
        fireBothFailed(`url_scan_${failureCode}`);
      } else if (err instanceof apiClient.ApiError && err.status === 409) {
        const conflicts = err.response?.details?.conflicts;
        if (Array.isArray(conflicts)) {
          const conflictMessages = formatConflictMessages(
            conflicts.filter(isUploadConflictItem),
            t,
          );

          if (conflictMessages) {
            onError(
              t("upload:errors.duplicateMessage", { conflicts: conflictMessages }),
            );
            fireUploadFailed("Duplicate files");
          } else {
            onError(t("upload:errors.duplicateFiles"));
            fireUploadFailed(err.message);
          }
        } else {
          onError(t("upload:errors.duplicateFiles"));
          fireUploadFailed(err.message);
        }
      } else if (err instanceof apiClient.ApiError && err.status === 402) {
        onError(t("errors:billing.insufficientCredits"));
        fireUploadFailed("insufficient_credits");
      } else if (err instanceof apiClient.ApiError) {
        const message = inputMode === "text"
          ? t("upload:textInput.errors.submitFailed")
          : inputMode === "url"
          ? t("upload:urlInput.errors.submitFailed")
          : t("upload:errors.uploadFailed");
        onError(message);
        fireBothFailed(err.message);
      } else {
        const message = t("upload:errors.unexpectedError");
        onError(message);
        fireBothFailed(message);
      }
      setIsUploading(false);
    }
  }, [
    inputMode,
    selectedFiles,
    textData,
    urlData,
    contextValues,
    groupImages,
    onError,
    onNavigate,
    t,
  ]);

  return { isUploading, handleSubmit };
}
